import { useTranslation } from 'react-i18next';

import { i18n } from '@/shared/i18n/instance';
import { DEFAULT_LOCALE, isLocale, matchLocale } from '@/shared/i18n/locales';
import type { Locale } from '@/shared/i18n/locales';
import { deviceStorage } from '@/shared/services/storage/device-storage';

/**
 * La langue de l'application : la choisir, la retrouver, la lire.
 *
 * C'est le seul propriétaire de la langue courante. Pas de copie dans un store
 * Zustand : i18next tient déjà cet état, prévient au changement et redessine
 * les écrans par `useTranslation()`. En doubler la valeur ailleurs, ce serait
 * ouvrir la porte aux deux vérités.
 *
 * L'ordre de décision, une fois pour toutes :
 *
 *   1. le choix fait dans l'application, s'il y en a un — il gagne toujours ;
 *   2. sinon la langue du compte, telle que le serveur la connaît ;
 *   3. sinon la langue du téléphone, si c'est l'une des nôtres ;
 *   4. sinon le français.
 */
const LOCALE_KEY = 'bul.locale';

export { i18n, translate, currentLocale, acceptLanguage } from '@/shared/i18n/instance';
export {
  DEFAULT_LOCALE,
  LOCALES,
  LOCALE_NAMES,
  fromApiLanguage,
  isLocale,
  toApiLanguage,
} from '@/shared/i18n/locales';
export type { ApiLanguage, Locale } from '@/shared/i18n/locales';
export * from '@/shared/i18n/format';

/** Ce que la personne a choisi ici, ou `null` si elle n'a jamais choisi. */
async function readChoice(): Promise<Locale | null> {
  const stored = await deviceStorage.read(LOCALE_KEY).catch(() => null);
  return isLocale(stored) ? stored : null;
}

/** La langue du navigateur, si c'est l'une des nôtres (dans l'ordre de ses préférences). */
function deviceLocale(): Locale | null {
  const tags = typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language]);
  for (const tag of tags) {
    const locale = matchLocale(tag);
    if (locale) {
      return locale;
    }
  }
  return null;
}

/**
 * Pose la langue au démarrage, avant le premier écran.
 *
 * À attendre dans la mise en page racine, comme les polices : ouvrir en
 * français pour sauter en malgache au rendu suivant se verrait, et donnerait
 * l'impression d'une application qui hésite.
 */
export async function initI18n(): Promise<void> {
  const chosen = (await readChoice()) ?? deviceLocale() ?? DEFAULT_LOCALE;
  if (chosen !== i18n.language) {
    await i18n.changeLanguage(chosen);
  }
}

/**
 * Change la langue, et s'en souvient.
 *
 * L'écriture ne fait pas attendre l'écran : l'interface a déjà basculé, et
 * l'échec du stockage ne coûte qu'un choix à refaire au prochain lancement.
 */
export async function setLocale(locale: Locale): Promise<void> {
  await i18n.changeLanguage(locale);
  void deviceStorage.write(LOCALE_KEY, locale).catch(() => undefined);
}

/**
 * Adopte la langue du compte — **seulement** si aucun choix n'a été fait sur
 * cet appareil.
 *
 * C'est ce qui fait qu'un vendeur qui se connecte sur un téléphone neuf
 * retrouve le malgache sans rien régler, et que le choix fait ici ne se fait
 * pas écraser à la première réponse du serveur.
 */
export async function adoptAccountLocale(language: string | null | undefined): Promise<void> {
  const account = matchLocale(language?.toLowerCase());
  if (!account || (await readChoice()) !== null || account === i18n.language) {
    return;
  }
  await i18n.changeLanguage(account);
}

/**
 * La langue affichée, dans un composant.
 *
 * Passe par `useTranslation()` : c'est lui qui abonne le composant au
 * changement de langue. Lire `i18n.language` directement rendrait un écran qui
 * ne se redessine jamais.
 */
export function useLocale(): Locale {
  const { i18n: instance } = useTranslation();
  return isLocale(instance.resolvedLanguage) ? instance.resolvedLanguage : DEFAULT_LOCALE;
}
